import { createFileRoute, Link, useNavigate, useParams } from '@tanstack/react-router';
import {
  Accordion, Alert, Anchor, Badge, Button, Group, Loader, NumberInput, Select, Stack, Switch, Text, TextInput, Textarea, Title,
} from '@mantine/core';
import { IconArrowLeft, IconBuilding, IconInfoCircle, IconTrash } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from '@mantine/form';
import { modals } from '@mantine/modals';
import { useEffect } from 'react';
import { deleteOne, fetchList, fetchOne, updateOne } from '@/shared/api/jsonapi/client';
import { JsonApiHttpError } from '@/shared/api/jsonapi/errors';
import { notifySuccess, notifyError } from '@/shared/lib/notify';
import { useDirtyFormGuard } from '@/shared/hooks/useDirtyFormGuard';
import { useIsSuperAdmin } from '@/shared/hooks/useIsSuperAdmin';
import type { Tenant } from '@/shared/types/entities';

export const Route = createFileRoute('/_app/admin/tenants/$tenantId')({ component: TenantDetailPage });

type RelationshipType = 'standalone' | 'holding_parent' | 'subsidiary' | 'affiliate' | 'joint_venture' | 'branch' | 'division';
type IndustryCodeSystem = 'ateco' | 'nace' | 'naics' | 'sic' | 'isic' | 'other';

interface FormVals {
  name: string;
  organization: string;
  description: string;
  email: string;
  certifiedEmail: string;
  phoneNumber: string;
  webSite: string;
  vatNumber: string;
  taxId: string;
  eInvoicingCode: string;
  defaultIban: string;
  owner: string;
  commercial: string;
  shareCapital: number | null;
  shareCapitalCurrency: string;
  plan: string;
  enabled: boolean;
  tenantType: string;
  legalForm: string;
  industryCode: string;
  industryCodeSystem: IndustryCodeSystem | '';
  ccnl: string;
  defaultCurrency: string;
  defaultLang: string;
  timezone: string;
  country: string;
  fiscalYearStartMonth: number | null;
  fiscalYearStartDay: number | null;
  // Holding
  relationshipType: RelationshipType | '';
  parentTenantId: string | null;
  parentShare: number | null;
  isHoldingOnly: boolean;
}

const EMPTY: FormVals = {
  name: '', organization: '', description: '', email: '', certifiedEmail: '', phoneNumber: '', webSite: '',
  vatNumber: '', taxId: '', eInvoicingCode: '', defaultIban: '',
  owner: '', commercial: '', shareCapital: null, shareCapitalCurrency: 'EUR',
  plan: 'free', enabled: true,
  tenantType: '', legalForm: '', industryCode: '', industryCodeSystem: 'ateco', ccnl: '',
  defaultCurrency: 'EUR', defaultLang: 'it', timezone: 'Europe/Rome', country: 'IT',
  fiscalYearStartMonth: 1, fiscalYearStartDay: 1,
  relationshipType: 'standalone', parentTenantId: null, parentShare: null, isHoldingOnly: false,
};

const RELATIONSHIP_TYPES: { value: RelationshipType; label: string }[] = [
  { value: 'standalone', label: 'Standalone' },
  { value: 'holding_parent', label: 'Capogruppo (holding)' },
  { value: 'subsidiary', label: 'Controllata' },
  { value: 'affiliate', label: 'Collegata' },
  { value: 'joint_venture', label: 'Joint venture' },
  { value: 'branch', label: 'Filiale' },
  { value: 'division', label: 'Divisione' },
];

const INDUSTRY_SYSTEMS: { value: IndustryCodeSystem; label: string }[] = [
  { value: 'ateco', label: 'ATECO (IT)' },
  { value: 'nace', label: 'NACE (EU)' },
  { value: 'naics', label: 'NAICS (US/CA/MX)' },
  { value: 'sic', label: 'SIC (US)' },
  { value: 'isic', label: 'ISIC (UN)' },
  { value: 'other', label: 'Altro' },
];

function TenantDetailPage() {
  const { tenantId } = useParams({ from: '/_app/admin/tenants/$tenantId' });
  const navigate = useNavigate();
  const qc = useQueryClient();
  const isSuperAdmin = useIsSuperAdmin();

  const tenantQ = useQuery({
    queryKey: ['tenant', tenantId],
    enabled: Boolean(tenantId),
    queryFn: () => fetchOne<Tenant>('tenants', tenantId),
  });
  const tenant = tenantQ.data;

  const parentsQ = useQuery({
    queryKey: ['tenants-parents-pick', tenantId],
    enabled: Boolean(tenantId),
    queryFn: () => fetchList<Tenant>('tenants', { page: { size: 200 } }),
  });

  const form = useForm<FormVals>({ mode: 'controlled', initialValues: EMPTY });
  useEffect(() => {
    if (!tenant) return;
    const t = tenant as unknown as Record<string, unknown>;
    const str = (k: string) => (typeof t[k] === 'string' ? (t[k] as string) : '');
    const bool = (k: string) => Boolean(t[k]);
    const num = (k: string) => (typeof t[k] === 'number' ? (t[k] as number) : null);
    const parentRel = (t.parentTenant as { id?: string } | null | undefined);
    const vals: FormVals = {
      name: str('name'), organization: str('organization'), description: str('description'),
      email: str('email'), certifiedEmail: str('certifiedEmail'), phoneNumber: str('phoneNumber'), webSite: str('webSite'),
      vatNumber: str('vatNumber'), taxId: str('taxId'), eInvoicingCode: str('eInvoicingCode'), defaultIban: str('defaultIban'),
      owner: str('owner'), commercial: str('commercial'),
      shareCapital: num('shareCapital'), shareCapitalCurrency: str('shareCapitalCurrency') || 'EUR',
      plan: str('plan') || 'free', enabled: bool('enabled'),
      tenantType: str('tenantType'), legalForm: str('legalForm'),
      industryCode: str('industryCode'), industryCodeSystem: (str('industryCodeSystem') || 'ateco') as IndustryCodeSystem,
      ccnl: str('ccnl'),
      defaultCurrency: str('defaultCurrency') || 'EUR',
      defaultLang: str('defaultLang') || 'it',
      timezone: str('timezone') || 'Europe/Rome',
      country: str('country') || 'IT',
      fiscalYearStartMonth: num('fiscalYearStartMonth') ?? 1,
      fiscalYearStartDay: num('fiscalYearStartDay') ?? 1,
      relationshipType: (str('relationshipType') || 'standalone') as RelationshipType,
      parentTenantId: parentRel?.id ?? null,
      parentShare: num('parentShare'),
      isHoldingOnly: bool('isHoldingOnly'),
    };
    form.setValues(vals);
    // Allinea lo stato "pulito" ai dati appena caricati: il dirty-guard deve
    // scattare solo su modifiche reali dell'utente, non sul populate iniziale.
    form.resetDirty(vals);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenant?.id]);

  const save = useMutation({
    mutationFn: (v: FormVals) => {
      const { parentTenantId, relationshipType, industryCodeSystem, ...rest } = v;
      const attributes: Record<string, unknown> = {
        ...rest,
        relationshipType: relationshipType || null,
        industryCodeSystem: industryCodeSystem || null,
      };
      const relationships: Record<string, { data: { type: string; id: string } | null }> = {};
      if (parentTenantId) {
        relationships.parentTenant = { data: { type: 'tenants', id: parentTenantId } };
      } else {
        relationships.parentTenant = { data: null };
      }
      return updateOne<Tenant>('tenants', { id: tenantId, attributes, relationships });
    },
    onSuccess: () => {
      notifySuccess('Tenant aggiornato');
      form.resetDirty();
      qc.invalidateQueries({ queryKey: ['tenant', tenantId] });
      qc.invalidateQueries({ queryKey: ['jsonapi', 'tenants'] });
    },
    // BE v1.9: la validazione holding/internazionale risponde 422 con source.pointer
    // sul campo incoerente — la mostriamo puntuale sul form, non solo a toast.
    onError: (err) => {
      if (err instanceof JsonApiHttpError && err.isValidationError()) {
        const fieldErrors = err.toFieldErrors();
        if (Object.keys(fieldErrors).length > 0) {
          form.setErrors(fieldErrors);
          return;
        }
      }
      notifyError(err);
    },
  });

  const remove = useMutation({
    mutationFn: () => deleteOne('tenants', tenantId),
    onSuccess: () => {
      notifySuccess('Tenant eliminato');
      qc.invalidateQueries({ queryKey: ['jsonapi', 'tenants'] });
    },
    onError: (err) => notifyError(err, 'Eliminazione fallita (forse non eliminabile o ha riferimenti)'),
  });

  // Protegge il form da uscite accidentali con modifiche non salvate.
  // Disattivato dopo l'eliminazione: il redirect post-delete non va bloccato.
  useDirtyFormGuard(form.isDirty() && !remove.isSuccess);

  // Navigazione post-delete in effect (non in onSuccess) così il blocker
  // viene ri-registrato con il guard disattivato prima del navigate.
  useEffect(() => {
    if (remove.isSuccess) void navigate({ to: '/admin/tenants' });
  }, [remove.isSuccess, navigate]);

  if (tenantQ.isLoading) return <Loader />;
  if (!tenant) return <Stack><Title order={2}>Tenant non trovato</Title></Stack>;

  const parentOptions = (parentsQ.data?.data ?? [])
    .filter((p) => p.id !== tenantId)
    .map((p) => ({ value: p.id, label: (p.name as string) ?? p.id }));

  const isSubsidiary = form.values.relationshipType === 'subsidiary' || form.values.relationshipType === 'affiliate' || form.values.relationshipType === 'branch' || form.values.relationshipType === 'division';

  return (
    <Stack>
      <Anchor component={Link} to="/admin/tenants" size="sm">
        <Group gap={4}><IconArrowLeft size={14} /> Torna alla lista tenant</Group>
      </Anchor>
      <Group justify="space-between">
        <Group>
          <IconBuilding size={28} />
          <div>
            <Title order={2}>{tenant.name ?? tenantId}</Title>
            <Group gap="xs" mt={4}>
              <Badge variant="light">{tenant.plan ?? 'free'}</Badge>
              {tenant.enabled ? <Badge color="green">Attivo</Badge> : <Badge color="red">Disattivato</Badge>}
              {tenant.isHoldingOnly && <Badge color="grape" variant="light">Holding only</Badge>}
              {tenant.isSystemTenant && <Badge color="orange" variant="light">System</Badge>}
            </Group>
          </div>
        </Group>
        {isSuperAdmin && (
          <Group gap="xs">
            <Button component={Link} to={`/admin/tenants/${tenantId}/subscription`} variant="light">
              Sottoscrizione & licenze
            </Button>
            <Button
              variant="light"
              color="red"
              leftSection={<IconTrash size={16} />}
              loading={remove.isPending}
              onClick={() => {
                modals.openConfirmModal({
                  title: 'Elimina tenant',
                  children: (
                    <Text size="sm">
                      Eliminare il tenant &quot;{tenant.name}&quot;? Vengono rimosse anche tutte le sue dipendenze.
                    </Text>
                  ),
                  labels: { confirm: 'Elimina', cancel: 'Annulla' },
                  confirmProps: { color: 'red' },
                  onConfirm: () => remove.mutate(),
                });
              }}
            >
              Elimina tenant
            </Button>
          </Group>
        )}
      </Group>

      <Alert color="blue" variant="light" icon={<IconInfoCircle size={16} />}>
        Sede legale, sede operativa e sede di fatturazione si gestiscono ora da <Anchor component={Link} to="/workforce/sites">Sedi aziendali</Anchor> usando i flag <code>isRegisteredOffice</code>, <code>isAdministrativeOffice</code>, <code>isMainOffice</code>, <code>isBillingAddress</code> (massimo una sede per flag).
      </Alert>

      <form onSubmit={form.onSubmit((v) => save.mutate(v))}>
        <Accordion defaultValue={['anagrafica', 'plan']} multiple variant="separated">
          <Accordion.Item value="anagrafica">
            <Accordion.Control>Anagrafica</Accordion.Control>
            <Accordion.Panel>
              <Stack>
                <Group grow>
                  <TextInput label="Nome" required {...form.getInputProps('name')} />
                  <TextInput label="Organizzazione" {...form.getInputProps('organization')} />
                </Group>
                <Textarea label="Descrizione" autosize minRows={2} {...form.getInputProps('description')} />
                <Group grow>
                  <Select
                    label="Tipo"
                    data={[{ value: '', label: '—' }, { value: 'HOLDING', label: 'Holding' }, { value: 'COMPANY', label: 'Company' }]}
                    {...form.getInputProps('tenantType')}
                  />
                  <TextInput label="Forma giuridica" placeholder="es. SRL, SPA" {...form.getInputProps('legalForm')} />
                </Group>
                <Group grow>
                  <Select
                    label="Sistema codice settore"
                    data={INDUSTRY_SYSTEMS}
                    {...form.getInputProps('industryCodeSystem')}
                  />
                  <TextInput label="Codice settore" placeholder="es. 62.01.00 (ATECO)" {...form.getInputProps('industryCode')} />
                </Group>
                <TextInput label="CCNL applicato" {...form.getInputProps('ccnl')} />
              </Stack>
            </Accordion.Panel>
          </Accordion.Item>

          <Accordion.Item value="contatti">
            <Accordion.Control>Contatti</Accordion.Control>
            <Accordion.Panel>
              <Stack>
                <Group grow>
                  <TextInput label="Email" {...form.getInputProps('email')} />
                  <TextInput label="PEC" {...form.getInputProps('certifiedEmail')} />
                </Group>
                <Group grow>
                  <TextInput label="Telefono" {...form.getInputProps('phoneNumber')} />
                  <TextInput label="Sito web" {...form.getInputProps('webSite')} />
                </Group>
                <Group grow>
                  <TextInput label="Owner" {...form.getInputProps('owner')} />
                  <TextInput label="Referente commerciale" {...form.getInputProps('commercial')} />
                </Group>
              </Stack>
            </Accordion.Panel>
          </Accordion.Item>

          <Accordion.Item value="fiscal">
            <Accordion.Control>Dati fiscali</Accordion.Control>
            <Accordion.Panel>
              <Stack>
                <Group grow>
                  <TextInput label="P.IVA" {...form.getInputProps('vatNumber')} />
                  <TextInput label="Codice fiscale" {...form.getInputProps('taxId')} />
                </Group>
                <Group grow>
                  <TextInput label="Codice SDI / e-Invoicing" {...form.getInputProps('eInvoicingCode')} />
                  <TextInput label="IBAN di default" {...form.getInputProps('defaultIban')} />
                </Group>
                <Group grow>
                  <NumberInput label="Capitale sociale" decimalScale={2} {...form.getInputProps('shareCapital')} />
                  <Select
                    label="Valuta capitale"
                    data={['EUR', 'USD', 'GBP', 'CHF'].map((c) => ({ value: c, label: c }))}
                    {...form.getInputProps('shareCapitalCurrency')}
                  />
                </Group>
                <Group grow>
                  <NumberInput
                    label="Mese inizio esercizio fiscale"
                    description="1 = gennaio"
                    min={1}
                    max={12}
                    {...form.getInputProps('fiscalYearStartMonth')}
                  />
                  <NumberInput
                    label="Giorno inizio esercizio fiscale"
                    min={1}
                    max={31}
                    {...form.getInputProps('fiscalYearStartDay')}
                  />
                </Group>
              </Stack>
            </Accordion.Panel>
          </Accordion.Item>

          <Accordion.Item value="holding">
            <Accordion.Control>Struttura societaria</Accordion.Control>
            <Accordion.Panel>
              <Stack>
                <Group grow>
                  <Select
                    label="Tipo di relazione"
                    data={RELATIONSHIP_TYPES}
                    {...form.getInputProps('relationshipType')}
                  />
                  <Switch
                    label="Solo holding (no operatività)"
                    description="Tenant esiste per consolidamento, non ha utenti operativi"
                    mt={24}
                    {...form.getInputProps('isHoldingOnly', { type: 'checkbox' })}
                  />
                </Group>
                <Group grow>
                  <Select
                    label="Capogruppo (parent tenant)"
                    placeholder={isSubsidiary ? 'Seleziona capogruppo' : 'Standalone — nessuna capogruppo'}
                    data={parentOptions}
                    clearable
                    searchable
                    disabled={!isSubsidiary}
                    {...form.getInputProps('parentTenantId')}
                  />
                  <NumberInput
                    label="Quota di partecipazione (%)"
                    description="Range 0.01–100"
                    decimalScale={2}
                    min={0.01}
                    max={100}
                    disabled={!form.values.parentTenantId}
                    {...form.getInputProps('parentShare')}
                  />
                </Group>
              </Stack>
            </Accordion.Panel>
          </Accordion.Item>

          <Accordion.Item value="plan">
            <Accordion.Control>Piano, internazionalità & stato</Accordion.Control>
            <Accordion.Panel>
              <Stack>
                <Group grow>
                  <Select
                    label="Piano"
                    data={[
                      { value: 'free', label: 'Free' },
                      { value: 'starter', label: 'Starter' },
                      { value: 'basic', label: 'Basic' },
                      { value: 'pro', label: 'Pro' },
                      { value: 'enterprise', label: 'Enterprise' },
                    ]}
                    {...form.getInputProps('plan')}
                  />
                  <Switch label="Tenant attivo" {...form.getInputProps('enabled', { type: 'checkbox' })} />
                </Group>
                <Group grow>
                  <TextInput
                    label="Paese (ISO 3166-1 alpha-2)"
                    placeholder="IT"
                    maxLength={2}
                    {...form.getInputProps('country')}
                  />
                  <Select
                    label="Valuta di default (ISO 4217)"
                    data={['EUR', 'USD', 'GBP', 'CHF'].map((c) => ({ value: c, label: c }))}
                    {...form.getInputProps('defaultCurrency')}
                  />
                </Group>
                <Group grow>
                  <Select
                    label="Lingua di default (ISO)"
                    data={[{ value: 'it', label: 'Italiano' }, { value: 'en', label: 'English' }, { value: 'fr', label: 'Français' }, { value: 'de', label: 'Deutsch' }, { value: 'es', label: 'Español' }]}
                    {...form.getInputProps('defaultLang')}
                  />
                  <TextInput label="Timezone (IANA)" placeholder="Europe/Rome" {...form.getInputProps('timezone')} />
                </Group>
              </Stack>
            </Accordion.Panel>
          </Accordion.Item>
        </Accordion>

        <Group justify="flex-end" mt="lg">
          <Button variant="subtle" onClick={() => void navigate({ to: '/admin/tenants' })}>
            Annulla
          </Button>
          <Button type="submit" loading={save.isPending}>Salva</Button>
        </Group>
      </form>
    </Stack>
  );
}
